<template>
  <div class="app-toggle-wrap" :class="rootClasses">
    <label class="app-toggle" :class="{ 'app-toggle--disabled': isLocked }">
      <input
        :id="inputId"
        ref="input"
        type="checkbox"
        role="switch"
        v-bind="$attrs"
        :name="name"
        :checked="checked"
        :disabled="isLocked"
        :aria-describedby="describedBy"
        class="app-toggle__input"
        @change="onChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />

      <span class="app-toggle__track">
        <span class="app-toggle__knob">
          <!-- Plain CSS spinner, not a FontAwesome icon: FA's SVG-with-JS
               mode replaces <i> with its own <svg> outside Vue's tracking,
               so when `loading` flips false and Vue tries to remove the
               node it once rendered, that node is no longer the one FA
               left in the DOM — the spinner can linger as an orphan even
               after the toggle has visibly reverted. A plain element never
               gets swapped out from under Vue, so removal is exact. -->
          <span v-if="loading" class="app-toggle__spinner" aria-hidden="true"></span>
        </span>
      </span>

      <span v-if="hasLabel" class="app-toggle__label">
        <slot>{{ label }}</slot>
      </span>
    </label>

    <small
      v-if="help"
      :id="helpId"
      class="app-toggle__help"
      v-html="help"
    ></small>
  </div>
</template>

<script>
let uid = 0

/**
 * On/off switch — the control for a binary state the user can change
 * directly, as opposed to a checkbox (which is a form value submitted
 * later) or a badge (which only reports state).
 *
 * Same v-model contract as the other form controls (`value` + `input`).
 *
 * `loading` is the important one for switches backed by an API call:
 * the flip is not local, so the control must show the request is in
 * flight and refuse further clicks until it settles. Drive it from a
 * per-row pending flag and keep `value` bound to the server's answer —
 * that way a failed request leaves the switch where it really is.
 *
 *   <app-toggle
 *     :value="account.activated"
 *     :loading="togglingId === account.id"
 *     tone="success"
 *     @change="setActivated(account, $event)"
 *   />
 */
export default {
  name: 'AppToggle',

  inheritAttrs: false,

  props: {
    value:    { type: Boolean, default: false },
    label:    { type: String,  default: '' },
    help:     { type: String,  default: '' },
    disabled: { type: Boolean, default: false },
    // Request in flight: shows a spinner in the knob and blocks input.
    loading:  { type: Boolean, default: false },
    name:     { type: String,  default: '' },
    id:       { type: String,  default: '' },
    // `primary` is the app accent; `success` reads as "healthy / enabled"
    // and suits switches that gate a feature being live.
    tone: {
      type: String,
      default: 'primary',
      validator: (v) => ['primary', 'success'].includes(v),
    },
    // Matches app-checkbox's set, so the two line up in the same form row.
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
  },

  emits: ['input', 'change', 'blur', 'focus'],

  data() {
    return {
      autoId: `app-toggle-${++uid}`,
    }
  },

  computed: {
    checked()  { return !!this.value },
    isLocked() { return this.disabled || this.loading },
    inputId()  { return this.id || this.autoId },
    helpId()   { return `${this.inputId}-help` },
    hasLabel() { return !!this.label || !!this.$slots.default },

    describedBy() {
      return this.help ? this.helpId : null
    },

    rootClasses() {
      return [
        `app-toggle-wrap--${this.size}`,
        `app-toggle-wrap--${this.tone}`,
        {
          'app-toggle-wrap--disabled': this.disabled,
          'app-toggle-wrap--loading': this.loading,
        },
      ]
    },
  },

  watch: {
    // Clicking a checkbox flips its native `checked` property immediately —
    // that's the browser's own doing, before Vue sees anything. For an
    // async-backed switch (activate/deactivate over the network) the parent
    // deliberately does NOT flip `value` until the server confirms it. If the
    // request fails, `value` — and so `checked` — ends up EQUAL to what it
    // was before the click: no change from Vue's point of view, so a watch
    // on `checked` itself would never fire and the DOM would stay stuck on
    // the browser's optimistic flip.
    //
    // What genuinely changes across a failed round-trip is `loading`
    // (true → false once the request settles). Forcing the DOM to match
    // `checked` at exactly that moment — success or failure alike — is what
    // snaps a stuck toggle back to the truth.
    loading(isLoading) {
      if (isLoading) return
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.checked = this.checked
      })
    },
  },

  methods: {
    onChange(e) {
      const next = e.target.checked
      this.$emit('input', next)
      this.$emit('change', next)
    },

    focus() { if (this.$refs.input) this.$refs.input.focus() },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — mirrors app-checkbox so the two sit together.
$atg-text:        #2b3a4a;
$atg-text-muted:  #6c7a89;
$atg-bg-surface:  #ffffff;
$atg-bg-soft:     #f6f8fa;
$atg-off:         #cfd6dc;
$atg-off-hover:   #b6c0c9;
$atg-blue:        #1f73c1;
$atg-blue-focus:  #4392d4;
$atg-green:       #2da44e;
$atg-duration:    140ms;
$atg-ease:        cubic-bezier(.4, 0, .2, 1);

// Every size is derived from this one table, including how far the knob
// travels — that distance is `track - knob - 2 × inset`, so hand-writing it
// per size is how a switch ends up with the knob overshooting its track.
$atg-inset: 2px;
$atg-sizes: (
  sm: (track-w: 28px, track-h: 16px, knob: 12px, font: 12px),
  md: (track-w: 34px, track-h: 20px, knob: 16px, font: 13px),
  lg: (track-w: 44px, track-h: 26px, knob: 22px, font: 14px),
);

@keyframes app-toggle-spin {
  to { transform: rotate(360deg); }
}

.app-toggle-wrap {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;

  &--disabled { opacity: 0.6; }

  // Still shows the spinner, so keep it fully legible while it settles.
  &--loading { opacity: 1; }
}

.app-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &--disabled { cursor: not-allowed; }

  // Visually-hidden native input — keeps keyboard and screen-reader
  // behaviour (role="switch") without restyling a native control.
  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px;
    height: 1px;
    margin: -1px;
  }

  // Track and knob dimensions come from the size blocks below.
  &__track {
    position: relative;
    flex-shrink: 0;
    border-radius: 999px;
    background: $atg-off;
    transition:
      background $atg-duration $atg-ease,
      box-shadow $atg-duration $atg-ease;
  }

  &__knob {
    position: absolute;
    top: $atg-inset;
    left: $atg-inset;
    border-radius: 50%;
    background: $atg-bg-surface;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform $atg-duration $atg-ease;
  }

  // A ring built from borders, spun with a plain CSS animation — no icon
  // font/SVG dependency, so there's nothing for Vue and FontAwesome to
  // disagree about ownership of.
  &__spinner {
    display: block;
    width: 70%;
    height: 70%;
    border-radius: 50%;
    border: 1.5px solid rgba(108, 122, 137, 0.25);
    border-top-color: $atg-text-muted;
    animation: app-toggle-spin 0.6s linear infinite;
  }

  &__label {
    color: $atg-text;
    line-height: 1.4;
  }

  // ── Hover ──
  &:hover &__track { background: $atg-off-hover; }

  // ── On ── (knob travel lives in the size blocks below)
  &__input:checked + &__track {
    background: $atg-blue;
  }

  &:hover &__input:checked + &__track { background: darken($atg-blue, 5%); }

  // ── Focus ──
  &__input:focus-visible + &__track {
    box-shadow: 0 0 0 3px rgba(67, 146, 212, 0.20);
  }

  // ── Disabled ──
  &__input:disabled + &__track { background: $atg-bg-soft; box-shadow: inset 0 0 0 1px $atg-off; }
  &__input:disabled:checked + &__track { background: lighten($atg-blue, 30%); box-shadow: none; }
}

// ── Tone: success (on = healthy / live) ──
.app-toggle-wrap--success {
  .app-toggle__input:checked + .app-toggle__track { background: $atg-green; }
  .app-toggle:hover .app-toggle__input:checked + .app-toggle__track { background: darken($atg-green, 5%); }
  .app-toggle__input:disabled:checked + .app-toggle__track { background: lighten($atg-green, 30%); }
  .app-toggle__input:focus-visible + .app-toggle__track {
    box-shadow: 0 0 0 3px rgba(45, 164, 78, 0.22);
  }
}

// ── Sizes ──
@each $name, $spec in $atg-sizes {
  $track-w: map-get($spec, track-w);
  $knob:    map-get($spec, knob);

  .app-toggle-wrap--#{$name} {
    .app-toggle__track {
      width: $track-w;
      height: map-get($spec, track-h);
    }

    .app-toggle__knob {
      width: $knob;
      height: $knob;
    }

    .app-toggle__label { font-size: map-get($spec, font); }

    // Travel = track − knob − both insets, so the knob lands flush.
    .app-toggle__input:checked + .app-toggle__track .app-toggle__knob {
      transform: translateX($track-w - $knob - $atg-inset * 2);
    }
  }
}

.app-toggle__help {
  font-size: 11px;
  margin-top: 4px;
  color: $atg-text-muted;
  line-height: 1.4;
}
</style>
